import React from 'react';
import { useSelector } from 'react-redux';
import { Point } from '@printu/geom';
import { getElementFrame } from 'app/utils/DOM';
import { call, noop } from 'app/utils/function';
import { covertPointFromWorkspaceToVirtual } from 'app/utils/viewport';
const getElement = (ref) => {
    const { current } = ref;
    if (!current) {
        return null;
    }
    if (current instanceof HTMLElement) {
        return current;
    }
    return current.UNSAFE_getDOMNode();
};
export const useHandleDrag = (ref, handlers, options) => {
    const opts = React.useMemo(() => (Object.assign({ nodeClass: 'handle', idProp: 'id', workspace: 'canvas' }, options)), [options]);
    const drag = React.useRef(null);
    const viewport = useSelector((state) => state.viewport);
    const { onDragStart, onDrag, onDragEnd } = handlers;
    React.useEffect(() => {
        const el = getElement(ref);
        if (!el) {
            return noop;
        }
        const getEventPoint = (evt) => {
            const frame = getElementFrame(el);
            const designPoint = new Point(evt.clientX - frame.x, evt.clientY - frame.y);
            const virtualPoint = covertPointFromWorkspaceToVirtual(viewport, designPoint.clone(), opts.workspace);
            return {
                design: designPoint,
                virtual: virtualPoint,
            };
        };
        const applyPointMove = (evt) => {
            const { id, down, delta, initial } = drag.current;
            const { design } = getEventPoint(evt);
            const x = design.x + delta.x;
            const y = design.y + delta.y;
            const virtual = covertPointFromWorkspaceToVirtual(viewport, new Point(x, y), opts.workspace);
            call(onDrag, id, { design, virtual }, down, initial, evt);
            drag.current.initial = false;
        };
        const $onPointerDown = (evt) => {
            getLogger('HandleDrag', 'debug')('[Mouse][MouseDown] useHandleDrag');
            const { target } = evt;
            if (target instanceof HTMLElement) {
                const downPoint = getEventPoint(evt);
                let left = parseFloat(target.style.left);
                let top = parseFloat(target.style.top);
                if (Number.isNaN(left) || Number.isNaN(top)) {
                    const parentBounds = target.parentElement.getBoundingClientRect();
                    const targetBounds = target.getBoundingClientRect();
                    left =
                        targetBounds.x -
                            parentBounds.x +
                            targetBounds.width * 0.5;
                    top =
                        targetBounds.y -
                            parentBounds.y +
                            targetBounds.height * 0.5;
                }
                const id = target.getAttribute(opts.idProp);
                drag.current = {
                    id,
                    down: downPoint,
                    initial: true,
                    delta: {
                        x: left - downPoint.design.x,
                        y: top - downPoint.design.y,
                    },
                };
                call(onDragStart, id, downPoint, evt);
            }
            evt.stopImmediatePropagation();
        };
        const $onPointerMove = (evt) => {
            if (drag.current) {
                applyPointMove(evt);
                evt.stopImmediatePropagation();
            }
        };
        const $onPointerUp = (evt) => {
            if (drag.current) {
                if (evt instanceof PointerEvent) {
                    applyPointMove(evt);
                }
                drag.current = null;
            }
        };
        const eventHandlers = {
            pointermove: $onPointerMove,
            dragover: $onPointerMove,
            pointerup: $onPointerUp,
            pointercancel: $onPointerUp,
            contextmenu: $onPointerUp,
            visibilitychange: $onPointerUp,
        };
        const handleNodes = [
            ...el.querySelectorAll(`.${opts.nodeClass}`),
        ].filter(node => node instanceof HTMLElement);
        getLogger('HandleDrag', 'debug')('[Mouse][Register] useHandleDrag');
        handleNodes.forEach(node => {
            node.addEventListener('pointerdown', $onPointerDown, true);
        });
        Object.keys(eventHandlers).forEach(eventType => {
            document.addEventListener(eventType, eventHandlers[eventType], true);
        });
        return () => {
            getLogger('HandleDrag', 'debug')('[Mouse][Unregister] useHandleDrag');
            handleNodes.forEach(node => node.removeEventListener('pointerdown', $onPointerDown, true));
            Object.keys(eventHandlers).forEach(eventType => document.removeEventListener(eventType, eventHandlers[eventType], true));
        };
    }, [ref, opts, viewport, onDragStart, onDrag, onDragEnd]);
    return [];
};
